import React, { useState, useEffect } from 'react';
import { profileService, UserContactDetailsDto } from '../../../../services/profile.service';
import { toast } from 'react-toastify';
import { validateContactDetails } from '../../../../utils/investorValidation';
import { SharedFormContext } from '../shared/types';
import { useLiveErrorClearing } from '../shared/useLiveErrorClearing';
import { useDelegationPermissions } from '../../../../contexts/DelegationPermissionsContext';
import { agentReadOnlyNote } from '../../../../utils/delegationScope';
import { getPermissionErrorMessage } from '../../../../utils/apiClient';
import { PremiumSelect } from '../../../../components/PremiumSelect/PremiumSelect';
import { isResidentIndividual } from '../../../../config/profileVisibility';
import { contentService, MasterStateDto } from '../../../../services/content.service';

// Dropdown values are UPPERCASE — see convention in shared/constants.ts.
// Stored values are Laravel's exact Title-case strings (information-update.blade #proof_of_address)
// so migrated rows keep their selection and downstream consumers see Laravel-era casing.
// RI is investor-type dependent (user decision 2026-09-05): Aadhaar ONLY — the Laravel
// flow was NRI/OCI-centric and never had an RI proof list.
const CONTACT_PROOF_OPTIONS_NRI_OCI = [
  { value: 'Passport', label: 'Passport' },
  { value: 'Driving License', label: 'Driving License' },
  { value: 'Utility Bill issued within 2 months', label: 'Utility Bill issued within 2 months' },
  { value: 'Bank Statement issued within 2 months', label: 'Bank Statement issued within 2 months' },
];

const CONTACT_PROOF_OPTIONS_RI = [
  { value: 'Aadhaar', label: 'Aadhaar' },
];

const ALL_PROOF_OPTIONS = [...CONTACT_PROOF_OPTIONS_NRI_OCI, ...CONTACT_PROOF_OPTIONS_RI];

/** Map legacy uppercase stored values (our old option list) onto the canonical ones. */
const normalizeProofOfAddress = (v?: string | null): string => {
  if (!v) return '';
  const match = ALL_PROOF_OPTIONS.find((o) => o.value.toUpperCase() === v.trim().toUpperCase());
  return match ? match.value : v;
};

const ADDRESS_TYPE_OPTIONS = [
  { value: 'OFFICE', label: 'Office' },
  { value: 'BUSINESS', label: 'Business' },
  { value: 'RESIDENTIAL/BUSINESS', label: 'Residential/Business' },
  { value: 'UNSPECIFIED', label: 'Unspecified' },
];

/** Matches Laravel's readonly/disabled inputs, which render greyed out. */
const LOCKED_FIELD_STYLE: React.CSSProperties = { backgroundColor: '#e9ecef', color: '#495057', cursor: 'not-allowed' };

/**
 * Laravel stores "yes"/"no"; earlier builds of this form wrote "true"/"1".
 * Accept every spelling so existing rows keep their answer.
 */
const isSameAsPerm = (value?: string): boolean => {
  const v = (value ?? '').trim().toLowerCase();
  return v === 'yes' || v === 'true' || v === '1';
};

interface ContactDetailsFormProps {
  initialData: UserContactDetailsDto | null;
  onSave: () => void;
  canEdit: boolean;
  canSubmit: boolean;
  isProxyMode: boolean;
  sharedContext: SharedFormContext;
}

export const ContactDetailsForm: React.FC<ContactDetailsFormProps> = ({
  initialData,
  onSave,
  canEdit,
  isProxyMode,
  sharedContext
}) => {
  const delegationPerms = useDelegationPermissions();
  // "Correspondance Address is Same As Above" defaults to Yes until the investor answers.
  const [formData, setFormData] = useState<Partial<UserContactDetailsDto>>({ corrAddressSameAsPerm: 'yes' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const corrSameAsPerm = isSameAsPerm(formData.corrAddressSameAsPerm);

  // Registration-captured identity fields: locked iff they carried a value at hydration.
  const [identityLocked, setIdentityLocked] = useState<Record<string, boolean>>({});
  const lockIf = (key: string) => !!identityLocked[key];
  const lockStyle = (key: string): React.CSSProperties | undefined =>
    identityLocked[key] ? LOCKED_FIELD_STYLE : undefined;

  // Proof of Address list by investor type: RI = Aadhaar only; NRI/OCI = Laravel's four.
  const isRi = isResidentIndividual(sharedContext.dashboardData?.investor?.investorType ?? undefined);
  const proofOptions = isRi ? CONTACT_PROOF_OPTIONS_RI : CONTACT_PROOF_OPTIONS_NRI_OCI;

  // Laravel repopulates State from master_states whenever Country changes.
  // Two independent lists: permanent address and correspondence address.
  const [stateOptions, setStateOptions] = useState<{ value: string; label: string }[]>([]);
  const [corrStateOptions, setCorrStateOptions] = useState<{ value: string; label: string }[]>([]);

  const toStateOptions = (rows: MasterStateDto[]) =>
    rows
      .filter((s) => s.ssName)
      .map((s) => ({ value: s.ssName!, label: s.ssName!.toUpperCase() }));

  useEffect(() => {
    const country = formData.userCountry;
    if (!country) { setStateOptions([]); return; }
    let cancelled = false;
    contentService.getStates(country)
      .then((rows) => { if (!cancelled) setStateOptions(toStateOptions(rows)); })
      .catch(() => { if (!cancelled) setStateOptions([]); });
    return () => { cancelled = true; };
  }, [formData.userCountry]);

  useEffect(() => {
    const country = formData.corrUserCountry;
    if (!country) { setCorrStateOptions([]); return; }
    let cancelled = false;
    contentService.getStates(country)
      .then((rows) => { if (!cancelled) setCorrStateOptions(toStateOptions(rows)); })
      .catch(() => { if (!cancelled) setCorrStateOptions([]); });
    return () => { cancelled = true; };
  }, [formData.corrUserCountry]);

  const COUNTRY_OPTIONS = React.useMemo(
    () =>
      sharedContext.taxResidencyCountries
        .filter((c) => c.ssName)
        .map((c) => ({ value: c.ssName!, label: c.ssName! })),
    [sharedContext.taxResidencyCountries]
  );

  const ISD_OPTIONS = React.useMemo(
    () =>
      sharedContext.isdCodes
        .filter((c) => c.codeValue !== undefined && c.codeValue !== null)
        .map((c) => ({
          value: String(c.codeValue),
          label: `+${c.codeValue} (${c.countryName ?? ''})`.trim(),
          selectedLabel: `+${c.codeValue}`,
        })),
    [sharedContext.isdCodes]
  );

  useEffect(() => {
    if (initialData) {
      const hydrated: UserContactDetailsDto = {
        ...initialData,
        // Legacy rows stored the proof uppercase; remap to Laravel's Title-case values
        // so the dropdown still shows the saved selection.
        proofOfAddress: normalizeProofOfAddress(initialData.proofOfAddress),
        // Unanswered defaults to Yes; a saved "no" is kept.
        corrAddressSameAsPerm: String(initialData.corrAddressSameAsPerm ?? '').trim()
          ? initialData.corrAddressSameAsPerm
          : 'yes',
      };
      setFormData(hydrated);
      // Empty-stays-editable (2026-09-05, same rule as Personal Info): the identity trio
      // is locked ONLY when registration actually captured it. Self-registration is
      // email-only — phone/ISD arrive EMPTY, and locking an empty mandatory field made
      // this tab impossible to complete (seen live, investor 202609055463). Decided once
      // at hydration so typing never locks mid-edit; the save persists all three.
      const filled = (v: unknown) => !!(v && String(v).trim());
      setIdentityLocked({
        email: filled(initialData.email),
        isdCode: filled(initialData.isdCode),
        primaryPhone: filled(initialData.primaryPhone),
      });
    }
  }, [initialData]);

  /**
   * QA #16 — the residential/overseas address defaults its Country to the investor's Country
   * of Residence, which is already known and locked on Personal Info.
   *
   * Address lines, city and zip arrived pre-filled while Country and State sat empty, leaving a
   * half-filled address to patch by hand. Own effect (not hydration) because the countries list
   * loads asynchronously. A DEFAULT only: editable afterwards (people move), never overwriting
   * a saved value, and State is still the investor's to choose — nothing else implies it.
   */
  useEffect(() => {
    if (String(formData.userCountry ?? '').trim()) return;
    if (!sharedContext.taxResidencyCountries.length) return;
    const residence = sharedContext.personalInfo?.countryOfResidence
      || sharedContext.dashboardData?.investor?.countryOfResidence;
    if (!residence) return;
    const match = sharedContext.taxResidencyCountries
      .find((c) => (c.ssName || '').trim().toLowerCase() === String(residence).trim().toLowerCase());
    if (match?.ssName) {
      setFormData((prev) => (String(prev.userCountry ?? '').trim() ? prev : { ...prev, userCountry: match.ssName }));
    }
  }, [formData.userCountry, sharedContext.taxResidencyCountries, sharedContext.personalInfo, sharedContext.dashboardData]);

  // Corrected fields drop their error immediately (remove-only live re-check).
  useLiveErrorClearing(errors, setErrors, () => validateContactDetails(formData as UserContactDetailsDto), [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors = validateContactDetails(formData as UserContactDetailsDto);
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstError = Object.values(newErrors)[0];
      toast.error(firstError ? `Please fix: ${firstError}` : 'Please fix the errors in the form');
      return;
    }

    setErrors({});
    setSaving(true);

    try {
      await profileService.updateContactDetails(formData as UserContactDetailsDto);
      toast.success('Contact details updated');
      onSave();
    } catch (err: any) {
      const permissionError = getPermissionErrorMessage(err);
      if (permissionError) {
        toast.error(permissionError);
      } else {
        toast.error(err.response?.data?.message || 'Failed to update');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="investor-profile__card" onSubmit={handleSubmit}>
      {/* <h3>Contact Details</h3> */}
      <div className="investor-profile__grid">
        {/* Laravel order (blade Section6): identity block first — all three are
            captured at registration and rendered readonly / disabled. */}
        <div className="form-group">
          <label>Email ID <span className="text-danger">*</span></label>
          <input
            type="email"
            value={formData.email ?? ''}
            readOnly={lockIf('email')}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            data-keep-case
            className={errors.email ? 'form-control is-invalid' : 'form-control'}
            style={lockStyle('email')}
          />
          {errors.email && <div className="invalid-feedback">{errors.email}</div>}
        </div>

        <div className="form-group">
          <label>Country Code <span className="text-danger">*</span></label>
          <PremiumSelect
            value={formData.isdCode ?? ''}
            onChange={(val) => setFormData({ ...formData, isdCode: val })}
            options={ISD_OPTIONS}
            disabled={lockIf('isdCode')}
            searchable
          />
        </div>

        <div className="form-group">
          <label>Phone No <span className="text-danger">*</span></label>
          <input
            type="tel"
            value={formData.primaryPhone ?? ''}
            readOnly={lockIf('primaryPhone')}
            onChange={(e) => setFormData({
              ...formData,
              // digits only, registration-parity cap of 16
              primaryPhone: e.target.value.replace(/\D/g, '').slice(0, 16),
            })}
            className={errors.primaryPhone ? 'form-control is-invalid' : 'form-control'}
            style={lockStyle('primaryPhone')}
          />
          {errors.primaryPhone && <div className="invalid-feedback">{errors.primaryPhone}</div>}
        </div>

        <div className="form-group form-group--full">
          <h3 style={{ marginTop: '1rem' }}>Current/Residential/Overseas Address</h3>
        </div>

        <div className="form-group form-group--full">
          <label>Proof of Address <span className="text-danger">*</span></label>
          <PremiumSelect
            value={formData.proofOfAddress ?? ''}
            onChange={(val) => setFormData({ ...formData, proofOfAddress: val })}
            options={proofOptions}
            placeholder="Select"
            error={errors.proofOfAddress}
          />
        </div>

        <div className="form-group">
          <label>Address Line 1 <span className="text-danger">*</span></label>
          <input
            value={formData.addressLine1 ?? ''}
            onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
            className={errors.addressLine1 ? 'form-control is-invalid' : ''}
          />
          {errors.addressLine1 && <div className="invalid-feedback">{errors.addressLine1}</div>}
        </div>

        <div className="form-group">
          <label>Address Line 2</label>
          <input
            value={formData.addressLine2 ?? ''}
            onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Address Line 3</label>
          <input
            value={formData.addressLine3 ?? ''}
            onChange={(e) => setFormData({ ...formData, addressLine3: e.target.value })}
          />
        </div>

        {/* Laravel row order: country / state / city / zip. */}
        <div className="form-group">
          <label>Country <span className="text-danger">*</span></label>
          <PremiumSelect
            value={formData.userCountry ?? ''}
            onChange={(val) => setFormData({ ...formData, userCountry: val })}
            options={COUNTRY_OPTIONS}
            placeholder="Select Country"
            error={errors.userCountry}
            searchable
          />
        </div>

        {/* Always a select, as Laravel renders it — the option list is empty
            until a Country is chosen, then repopulates from master_states. */}
        <div className="form-group">
          <label>State <span className="text-danger">*</span></label>
          <PremiumSelect
            value={formData.userState ?? ''}
            onChange={(val) => setFormData({ ...formData, userState: val })}
            options={stateOptions}
            placeholder="Select State"
            error={errors.userState}
            searchable
          />
        </div>

        <div className="form-group">
          <label>City <span className="text-danger">*</span></label>
          <input
            value={formData.userCity ?? ''}
            onChange={(e) => setFormData({ ...formData, userCity: e.target.value })}
            className={errors.userCity ? 'form-control is-invalid' : ''}
          />
          {errors.userCity && <div className="invalid-feedback">{errors.userCity}</div>}
        </div>

        <div className="form-group">
          <label>Postal/ Zip code <span className="text-danger">*</span></label>
          <input
            value={formData.userZipCode ?? ''}
            onChange={(e) => setFormData({ ...formData, userZipCode: e.target.value })}
            className={errors.userZipCode ? 'form-control is-invalid' : ''}
          />
          {errors.userZipCode && <div className="invalid-feedback">{errors.userZipCode}</div>}
        </div>

        {/* Laravel renders this as Yes/No radios, storing "yes"/"no". */}
        <div className="form-group form-group--full">
          <label>Correspondance Address is Same As Above <span className="text-danger">*</span></label>
          <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.4rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 'normal' }}>
              <input
                type="radio"
                name="corrAddressSameAsPerm"
                value="yes"
                checked={corrSameAsPerm}
                onChange={() => setFormData({ ...formData, corrAddressSameAsPerm: 'yes' })}
              /> Yes
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 'normal' }}>
              <input
                type="radio"
                name="corrAddressSameAsPerm"
                value="no"
                checked={!corrSameAsPerm}
                onChange={() => setFormData({ ...formData, corrAddressSameAsPerm: 'no' })}
              /> No
            </label>
          </div>
        </div>

        {/* Correspondence Address - Show only if different from permanent */}
        {!corrSameAsPerm && (
          <>
            <div className="form-group">
              <label>Address Type <span className="text-danger">*</span></label>
              <PremiumSelect
                value={formData.addressType ?? ''}
                onChange={(val) => setFormData({ ...formData, addressType: val })}
                options={ADDRESS_TYPE_OPTIONS}
                error={errors.addressType}
              />
            </div>
            <div className="form-group">
              <label>Address Line 1 <span className="text-danger">*</span></label>
              <input
                value={formData.corrAddressLine1 ?? ''}
                onChange={(e) => setFormData({ ...formData, corrAddressLine1: e.target.value })}
                className={errors.corrAddressLine1 ? 'form-control is-invalid' : ''}
              />
              {errors.corrAddressLine1 && <div className="invalid-feedback">{errors.corrAddressLine1}</div>}
            </div>
            <div className="form-group">
              <label>Address Line 2</label>
              <input
                value={formData.corrAddressLine2 ?? ''}
                onChange={(e) => setFormData({ ...formData, corrAddressLine2: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Address Line 3</label>
              <input
                value={formData.corrAddressLine3 ?? ''}
                onChange={(e) => setFormData({ ...formData, corrAddressLine3: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Country <span className="text-danger">*</span></label>
              <PremiumSelect
                value={formData.corrUserCountry ?? ''}
                onChange={(val) => setFormData({ ...formData, corrUserCountry: val })}
                options={COUNTRY_OPTIONS}
                placeholder="Select Country"
                error={errors.corrUserCountry}
                searchable
              />
            </div>
            <div className="form-group">
              <label>State <span className="text-danger">*</span></label>
              <PremiumSelect
                value={formData.corrUserState ?? ''}
                onChange={(val) => setFormData({ ...formData, corrUserState: val })}
                options={corrStateOptions}
                placeholder="Select State"
                error={errors.corrUserState}
                searchable
              />
            </div>
            <div className="form-group">
              <label>City <span className="text-danger">*</span></label>
              <input
                value={formData.corrUserCity ?? ''}
                onChange={(e) => setFormData({ ...formData, corrUserCity: e.target.value })}
                className={errors.corrUserCity ? 'form-control is-invalid' : ''}
              />
              {errors.corrUserCity && <div className="invalid-feedback">{errors.corrUserCity}</div>}
            </div>
            <div className="form-group">
              <label>Postal/ zip code<span className="text-danger">*</span></label>
              <input
                value={formData.corrUserZipCode ?? ''}
                onChange={(e) => setFormData({ ...formData, corrUserZipCode: e.target.value })}
                className={errors.corrUserZipCode ? 'form-control is-invalid' : ''}
              />
              {errors.corrUserZipCode && <div className="invalid-feedback">{errors.corrUserZipCode}</div>}
            </div>
          </>
        )}
      </div>

      {!sharedContext.lockedAfterSubmit && (
        <button type="submit" className="btn-save" disabled={saving || !canEdit}>
        {saving ? 'Submitting...' : 'Submit'}
        </button>
      )}
      {!canEdit && agentReadOnlyNote(delegationPerms, 'KYC') && (
        <small className="text-warning d-block mt-2">
          {agentReadOnlyNote(delegationPerms, 'KYC')}
        </small>
      )}
    </form>
  );
};
